import axios, { AxiosError } from 'axios';
import type {
  CategoryOption,
  SavedVideo,
  SnapCodeResponse,
  VideoAnalysisJob,
  VideoCategoriesResponse,
  VideoPlayback,
} from '@/types/videoAds';
import { redirectToLoginAfterAuthFailure } from '@/utils/authSession';

const apiBaseUrl = import.meta.env.VITE_API_ENDPOINT
  || import.meta.env.VITE_API_BASE_URL
  || 'http://localhost:8000';
const configuredTimeout = Number(import.meta.env.VITE_API_TIMEOUT);

const resolveMediaUrl = (url?: string | null) => {
  if (!url || /^https?:\/\//i.test(url)) return url;
  return `${apiBaseUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
};

const videoApi = axios.create({
  baseURL: apiBaseUrl,
  // Axios uses 0 to disable request timeouts.
  timeout: Number.isFinite(configuredTimeout) && configuredTimeout >= 0
    ? configuredTimeout
    : 15_000,
  headers: { 'Content-Type': 'application/json' },
});

videoApi.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

videoApi.interceptors.response.use(
  response => response,
  error => {
    if (error instanceof AxiosError && error.response?.status === 401) {
      redirectToLoginAfterAuthFailure(error.response.data);
    }
    return Promise.reject(error);
  },
);

const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { detail?: string; message?: string; error?: string } | undefined;
    return data?.detail || data?.message || data?.error || error.message || 'The request failed.';
  }
  return error instanceof Error ? error.message : 'The request failed.';
};

class VideoAdsService {
  async analyze(url: string, userId: number): Promise<VideoAnalysisJob> {
    try {
      const response = await videoApi.post<VideoAnalysisJob>(
        '/analyze',
        { url },
        { params: { user_id: userId, save_to_db: true } },
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async upload(
    file: File,
    userId: number,
    onUploadProgress?: (progress: number) => void,
  ): Promise<VideoAnalysisJob> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await videoApi.post<VideoAnalysisJob>('/upload', formData, {
        // The server returns quickly once the
        // transfer finishes and analysis continues through the job poller.
        timeout: 0,
        params: {
          user_id: userId,
          save_to_db: true,
          language: 'en',
          frame_sample_rate: 30,
        },
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onUploadProgress
          ? (event) => {
              if (!event.total) return;
              onUploadProgress(Math.round((event.loaded * 100) / event.total));
            }
          : undefined,
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getJob(jobId: string): Promise<VideoAnalysisJob> {
    try {
      const response = await videoApi.get<VideoAnalysisJob>(`/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getUserVideos(userId: number): Promise<SavedVideo[]> {
    try {
      const response = await videoApi.get<SavedVideo[] | { videos: SavedVideo[] }>(
        `/users/${userId}/videos`,
      );
      const videos = Array.isArray(response.data) ? response.data : response.data.videos;
      return videos.map((video) => ({
        ...video,
        playback_url: resolveMediaUrl(video.playback_url),
        thumbnail_url: resolveMediaUrl(video.thumbnail_url),
      }));
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getVideoPlayback(videoId: number): Promise<VideoPlayback> {
    try {
      const response = await videoApi.get<VideoPlayback>(`/videos/${videoId}/playback`);
      return {
        ...response.data,
        playback_url: resolveMediaUrl(response.data.playback_url)!,
        thumbnail_url: resolveMediaUrl(response.data.thumbnail_url),
      };
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async deleteVideo(videoId: number): Promise<void> {
    try {
      await videoApi.delete(`/videos/${videoId}`);
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getVideoCategories(videoId: number): Promise<VideoCategoriesResponse> {
    try {
      const response = await videoApi.get<VideoCategoriesResponse>(`/videos/${videoId}/categories`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getCategoryOptions(): Promise<CategoryOption[]> {
    try {
      const response = await videoApi.get<CategoryOption[]>('/categories');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async addVideoCategory(videoId: number, categoryId: number): Promise<VideoCategoriesResponse> {
    try {
      const response = await videoApi.post<VideoCategoriesResponse>(
        `/videos/${videoId}/categories`,
        { category_id: categoryId },
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async removeVideoCategory(videoId: number, categoryId: number): Promise<VideoCategoriesResponse> {
    try {
      const response = await videoApi.delete<VideoCategoriesResponse>(
        `/videos/${videoId}/categories/${categoryId}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getSnapCode(videoId: number): Promise<SnapCodeResponse> {
    try {
      const response = await videoApi.get<SnapCodeResponse>(`/videos/${videoId}/snap-code`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async downloadSnapCodePdf(videoId: number): Promise<Blob> {
    try {
      const response = await videoApi.get<Blob>(`/videos/${videoId}/snap-code.pdf`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }
}

export const videoAdsService = new VideoAdsService();
